import React, { useEffect, useRef } from 'react';
import { useEmbeddedService } from '../hooks/useEmbeddedService';
import { useEmbeddingContext } from '../hooks/useEmbeddingContext';
import type { WebComponentLoaderProps } from '../../../types/embedding';
import { useShellI18n } from '@/features/language/useI18n';

export const WebComponentLoader: React.FC<WebComponentLoaderProps> = ({
  service,
  className,
  onLoad,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contextData = useEmbeddingContext();
  const { t } = useShellI18n();
  
  const {
    isLoading,
    error,
    onLoad: handleLoad,
    onError: handleError,
  } = useEmbeddedService(service);

  useEffect(() => {
    if (!service.url) {
      handleError(new Error('Web component URL is missing'));
      return;
    }

    const scriptId = `wc-script-${service.id}`;
    let script = document.getElementById(scriptId) as HTMLScriptElement;

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = service.url;
      script.async = true;
      script.onload = () => {
        handleLoad();
        onLoad?.();
      };
      script.onerror = () => {
        const err = new Error(`Failed to load web component script: ${service.url}`);
        handleError(err);
        onError?.(err);
      };
      document.body.appendChild(script);
    } else {
      // Script already loaded
      handleLoad();
      onLoad?.();
    }

    // Cleanup not typically needed for script tags as other components might use it
  }, [service.url, service.id, handleLoad, onLoad, handleError, onError]);

  // Render the custom element
  const renderCustomElement = () => {
    // Assuming the tag name is derived from service name or id if not explicitly provided
    // Ideally this should be in the service config, but for now we'll infer or require it in config
    // We'll use a data attribute for the tag name in the service config
    // In a real scenario, we'd add tagName to EmbeddedService type or config
    
    // For this implementation, we'll assume the service ID is the tag name
    // e.g., 'my-widget'
    const TagName = service.id;

    // Prepare properties to pass to the web component
    const props: Record<string, unknown> = {
      ...service.styles,
      class: className,
    };

    if (service.passAuth) {
      props['auth-context'] = JSON.stringify(contextData.auth);
    }

    if (service.passConsent) {
      props['consent-context'] = JSON.stringify(contextData.consent);
    }

    return React.createElement(TagName, {
      ref: (element: HTMLElement | null) => {
        if (element) {
          // Add event listeners or other imperative logic here if needed
        }
      },
      ...props
    });
  };

  if (error) {
    return (
      <div className="embed-error">
        <h3>{t('Failed to load {{name}}', { name: service.name })}</h3>
        <button
          onClick={() => window.location.reload()}
          className="btn ghost sm"
          type="button"
        >
          {t('Retry')}
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`embed-frame-wrap ${className || ''}`}>
      {isLoading && (
        <div className="embed-loading-overlay">
          <div className="embed-spinner" />
        </div>
      )}
      {renderCustomElement()}
    </div>
  );
};
